import { createAsyncThunk } from "@reduxjs/toolkit";
import { backend } from "Backend";
import {
  ClientLens,
  CollectPermission,
  CollectUserRole,
  FirmWithLenses,
  FirmsWithLensesDocument,
  GetClientLensDocument,
  GetLensesDocument,
  GetUsersDocument,
  GetUsersQuery,
  InviteCollectUserDocument,
  NamedFirm,
  NamedLens,
  SetUserCollectPermissionDocument,
  SimpleLensView,
  UpdateCollectUserFirmsDocument,
  UpdateCollectUserLensPermissionDocument,
  UpdateCollectUserRoleDocument,
  UpdateLensPermissionDocument,
  UpdateUserDeactivatedDocument,
  UpdateUserDeactivatedMutationVariables,
  DeleteFirmDocument,
  UpdateFirmMutation,
  UpdateFirmDocument,
  CreateFirmMutation,
  CreateFirmDocument,
  AddLensesToFirmMutation,
  AddLensesToFirmDocument,
  DeleteLensesFromFirmMutation,
  DeleteLensesFromFirmDocument,
  InviteCollectUserMutation,
  ResendCollectUserInviteDocument,
} from "generated/graphql";
import { runGqlMutation, runGqlQuery } from "gqlClient";
import type { RootState } from "redux/store";
import { extractGQLError } from "shared/PatternGraphQLError";

// Users Actions
interface InviteUserProps {
  email: string;
  admin: boolean;
  lenses: number[];
  firstName?: string;
  lastName?: string;
}

export const fetchUsers = createAsyncThunk<GetUsersQuery, undefined>("users/fetchAll", () => {
  return runGqlQuery(GetUsersDocument, {});
});

export const updateUserDeactivated = createAsyncThunk<
  UpdateUserDeactivatedMutationVariables,
  UpdateUserDeactivatedMutationVariables
>("users/updateUserDeactivated", async (input, thunkAPI) =>
  runGqlMutation(UpdateUserDeactivatedDocument, { ...input })
    .then(() => input)
    .catch((err) => thunkAPI.rejectWithValue(extractGQLError(err)))
);
export const inviteUser = createAsyncThunk<
  {
    admin: boolean;
    email: string;
    lenses: number[];
    firstName?: string;
    lastName?: string;
  },
  InviteUserProps
>("users/invite", async ({ email, admin, lenses, firstName, lastName }) => {
  await backend.inviteUser(email.toLowerCase(), admin, lenses, firstName, lastName);
  return { email, admin, lenses, firstName, lastName };
});

export const updateLensPermission = createAsyncThunk<
  { userId: number; lens: NamedLens; value: boolean },
  { userId: number; lens: NamedLens; value: boolean }
>("users/updateLensPermission", ({ userId, lens, value }) =>
  runGqlMutation(UpdateLensPermissionDocument, {
    userId,
    lensId: lens.id,
    status: value,
  }).then(() => ({
    userId,
    lens,
    value,
  }))
);

// Collect Users Actions

interface InviteCollectUserProps {
  email: string;
  firstName: string;
  lastName: string;
  lensIds: number[];
  firmIds: number[];
  userRole: CollectUserRole;
  permissions: CollectPermission[];
}

export const inviteCollectUser = createAsyncThunk<InviteCollectUserMutation, InviteCollectUserProps>(
  "collectUsers/inviteCollectUser",
  async (values) => runGqlMutation(InviteCollectUserDocument, values)
);

interface UpdateLensPermission {
  collectUserId: number;
  lensPermissions: {
    lensId: number;
    status: boolean;
  }[];
}

export const resendCollectInvite = createAsyncThunk<{ email: string }, { email: string }>(
  "collectUsers/resendInvite",
  async ({ email }) => runGqlMutation(ResendCollectUserInviteDocument, { email }).then(() => ({ email }))
);

export const updateCollectUserLensPermission = createAsyncThunk<UpdateLensPermission, UpdateLensPermission>(
  "collectUsers/updateLensPermission",
  async (body) =>
    Promise.all(
      body.lensPermissions.map((lensPermission) =>
        runGqlMutation(UpdateCollectUserLensPermissionDocument, {
          collectUserId: body.collectUserId,
          lensId: lensPermission.lensId,
          status: lensPermission.status,
        })
      )
    ).then(() => body)
);

interface UpdatePermission {
  collectUserId: number;
  permissions: {
    permission: CollectPermission;
    status: boolean;
  }[];
}
export const setCollectUserPermissions = createAsyncThunk<UpdatePermission, UpdatePermission>(
  "collectUsers/setUserCollectPermission",
  async (body) =>
    Promise.all(
      body.permissions.map((lensPermission) =>
        runGqlMutation(SetUserCollectPermissionDocument, {
          collectUserId: body.collectUserId,
          permission: lensPermission.permission,
          status: lensPermission.status,
        })
      )
    ).then(() => body)
);
export const updateCollectUserRole = createAsyncThunk<
  { collectUserId: number; role: CollectUserRole },
  { collectUserId: number; role: CollectUserRole }
>("collectUsers/updateRole", async (body) => runGqlMutation(UpdateCollectUserRoleDocument, body).then(() => body));

export const updateCollectUserFirms = createAsyncThunk<
  { collectUserId: number; firms: NamedFirm[] },
  { collectUserId: number; firms: NamedFirm[] }
>("collectUsers/updateFirms", async (body) =>
  runGqlMutation(UpdateCollectUserFirmsDocument, {
    collectUserId: body.collectUserId,
    firmIds: body.firms.map((f) => f.id),
  }).then(() => body)
);

// Firms Actions
export const fetchFirms = createAsyncThunk<FirmWithLenses[]>("app/fetchFirms", () => {
  return runGqlQuery(FirmsWithLensesDocument, {}).then((response) => response.firms.firmsWithLenses);
});

// Lenses Actions
export const fetchLenses = createAsyncThunk<SimpleLensView[]>("lenses/fetch", (_, thunkAPI) => {
  const lenses = (thunkAPI as { getState: () => RootState }).getState().client.lenses;

  if (lenses.length > 0) {
    return Promise.resolve(lenses);
  } else {
    return runGqlQuery(GetLensesDocument, {}).then((r) => r.lensesNew);
  }
});

export const fetchClientLens = createAsyncThunk<ClientLens, { lensId: number }, { state: RootState }>(
  "lenses/fetchClientLens",
  ({ lensId }, thunkAPI) => {
    const clientLens = thunkAPI.getState().client.clientLenses[lensId];

    return clientLens
      ? Promise.resolve(clientLens)
      : runGqlQuery(GetClientLensDocument, { lensId }).then((r) => r.lens.clientLens);
  }
);

export const deleteFirm = createAsyncThunk<number, { firmId: number }>("firmManager/deleteFirm", ({ firmId }) =>
  runGqlMutation(DeleteFirmDocument, { firmId }).then(() => firmId)
);

export const updateFirm = createAsyncThunk<
  UpdateFirmMutation,
  {
    id: number;
    clientId: number;
    name: string;
    address: string;
    attorneyName: string;
    phoneNumber: string;
    externalId: string;
  }
>("firmManager/upsertFirm", ({ id, clientId, name, address, attorneyName, phoneNumber, externalId }) =>
  runGqlMutation(UpdateFirmDocument, {
    id,
    clientId,
    name,
    address,
    attorneyName,
    phoneNumber,
    externalId,
  })
);

export const createFirm = createAsyncThunk<
  CreateFirmMutation,
  {
    clientId: number;
    name: string;
    address: string;
    attorneyName: string;
    phoneNumber: string;
    externalId: string;
  }
>("firmManager/createFirm", ({ clientId, name, address, attorneyName, phoneNumber, externalId }) =>
  runGqlMutation(CreateFirmDocument, {
    clientId,
    name,
    address,
    attorneyName,
    phoneNumber,
    externalId,
  })
);

export const addLensesToFirm = createAsyncThunk<AddLensesToFirmMutation, { firmId: number; lensIds: number[] }>(
  "firmManager/addLensesToFirm",
  ({ firmId, lensIds }) => runGqlMutation(AddLensesToFirmDocument, { firmId, lensIds })
);

export const deleteLensesFromFirm = createAsyncThunk<
  DeleteLensesFromFirmMutation,
  { firmId: number; lensIds: number[] }
>("firmManager/deleteLensesFromFirm", ({ firmId, lensIds }) =>
  runGqlMutation(DeleteLensesFromFirmDocument, { firmId, lensIds })
);
